import { StyleSheet, View, type ViewProps } from "react-native";

import {
  radii,
  spacing,
  palette,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";

type BoxSpace = "none" | "xs" | "sm" | "md" | "lg" | "xl";
type BoxSurface = "none" | "default" | "muted" | "forest" | "danger";
type BoxRadius = "none" | "sm" | "md" | "lg" | "pill";

export function Box({
  padding = "none",
  surface = "none",
  radius = "none",
  style,
  ...props
}: ViewProps & {
  padding?: BoxSpace;
  surface?: BoxSurface;
  radius?: BoxRadius;
}) {
  const { stylesheet, paddingStyles, surfaceStyles, radiusStyles } =
    useThemedStyles(makeStyles);
  return (
    <View
      style={[
        paddingStyles[padding],
        surfaceStyles[surface],
        radiusStyles[radius],
        style,
      ]}
      {...props}
    />
  );
}

const makeStyles = (colors: ThemeColors) => {
  const stylesheet = StyleSheet.create({
    paddingNone: { padding: 0 },
    paddingXs: { padding: spacing.xs },
    paddingSm: { padding: spacing.s },
    paddingMd: { padding: spacing.m },
    paddingLg: { padding: spacing.l },
    paddingXl: { padding: spacing.xl },
    surfaceNone: { backgroundColor: "transparent" },
    surfaceDefault: { backgroundColor: colors.background },
    surfaceMuted: { backgroundColor: colors.surface },
    surfaceForest: { backgroundColor: palette.brandForest },
    surfaceDanger: { backgroundColor: colors.dangerSurface },
    radiusNone: { borderRadius: 0 },
    radiusSm: { borderRadius: radii.s },
    radiusMd: { borderRadius: radii.m },
    radiusLg: { borderRadius: radii.l },
    radiusPill: { borderRadius: radii.pill },
  });
  const paddingStyles = {
    none: stylesheet.paddingNone,
    xs: stylesheet.paddingXs,
    sm: stylesheet.paddingSm,
    md: stylesheet.paddingMd,
    lg: stylesheet.paddingLg,
    xl: stylesheet.paddingXl,
  };
  const surfaceStyles = {
    none: stylesheet.surfaceNone,
    default: stylesheet.surfaceDefault,
    muted: stylesheet.surfaceMuted,
    forest: stylesheet.surfaceForest,
    danger: stylesheet.surfaceDanger,
  };
  const radiusStyles = {
    none: stylesheet.radiusNone,
    sm: stylesheet.radiusSm,
    md: stylesheet.radiusMd,
    lg: stylesheet.radiusLg,
    pill: stylesheet.radiusPill,
  };
  return { stylesheet, paddingStyles, surfaceStyles, radiusStyles };
};
